import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Clock, Users } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

import { planningAPI } from "../services/planningService";
import { employeesAPI } from "../services/api";
import CalendarEmployeeSelector from "./schedule/CalendarEmployeeSelector";
import CalendarNavigation from "./schedule/CalendarNavigation";
import CalendarStatsCards from "./schedule/CalendarStatsCards";
import CalendarDayGrid from "./schedule/CalendarDayGrid";

const EmployeeScheduleCalendar = () => {
  const { t, i18n } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("month");

  useEffect(() => { loadEmployees(); }, []);

  useEffect(() => {
    if (selectedEmployeeId) loadSchedule();
  }, [selectedEmployeeId, currentDate, viewMode]);

  const loadEmployees = async () => {
    try {
      const data = await employeesAPI.getEmployees({ limit: 1000 });
      setEmployees(data.employees || []);
    } catch (error) {
    }
  };

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const getDateRange = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    let startDate, endDate;
    if (viewMode === "week") {
      const day = currentDate.getDay();
      const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1);
      startDate = new Date(year, month, diff);
      endDate = new Date(year, month, diff + 6);
    } else if (viewMode === "month") {
      startDate = new Date(year, month, 1);
      endDate = new Date(year, month + 1, 0);
    } else {
      startDate = new Date(year, month, 1);
      endDate = new Date(year, month + 3, 0);
    }
    return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
  };

  const loadSchedule = async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = getDateRange();
      const data = await planningAPI.calculations.getEmployeeSchedule(selectedEmployeeId, startDate, endDate);
      setScheduleData(data);
    } catch (error) {
      toast.error(t("planning.legacy.calendar.errors.loadSchedule"));
    } finally {
      setLoading(false);
    }
  };

  const navigatePrevious = () => {
    const d = new Date(currentDate);
    if (viewMode === "week") d.setDate(d.getDate() - 7);
    else if (viewMode === "month") d.setMonth(d.getMonth() - 1);
    else d.setMonth(d.getMonth() - 3);
    setCurrentDate(d);
  };

  const navigateNext = () => {
    const d = new Date(currentDate);
    if (viewMode === "week") d.setDate(d.getDate() + 7);
    else if (viewMode === "month") d.setMonth(d.getMonth() + 1);
    else d.setMonth(d.getMonth() + 3);
    setCurrentDate(d);
  };

  const goToToday = () => setCurrentDate(new Date());

  const getViewModeLabel = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    if (viewMode === "week") {
      const { startDate, endDate } = getDateRange();
      return t("planning.legacy.calendar.weekRange", { start: startDate, end: endDate });
    }
    const monthLabel = new Date(year, month, 1).toLocaleDateString(i18n.language, { month: "long" });
    if (viewMode === "month") return t("planning.legacy.calendar.monthLabel", { month: monthLabel, year });
    const endMonthLabel = new Date(year, month + 2, 1).toLocaleDateString(i18n.language, { month: "long" });
    return t("planning.legacy.calendar.threeMonthLabel", { startMonth: monthLabel, endMonth: endMonthLabel, year });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{t("planning.legacy.calendar.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("planning.legacy.calendar.subtitle")}</p>
        </div>
        <Select value={viewMode} onValueChange={setViewMode}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="week">{t("planning.legacy.calendar.viewModes.week")}</SelectItem>
            <SelectItem value="month">{t("planning.legacy.calendar.viewModes.month")}</SelectItem>
            <SelectItem value="3months">{t("planning.legacy.calendar.viewModes.threeMonths")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <CalendarEmployeeSelector
        employees={employees}
        selectedEmployeeId={selectedEmployeeId}
        setSelectedEmployeeId={setSelectedEmployeeId}
      />

      {selectedEmployeeId && (
        <>
          <CalendarNavigation
            navigatePrevious={navigatePrevious}
            navigateNext={navigateNext}
            goToToday={goToToday}
            viewModeLabel={getViewModeLabel()}
          />

          <CalendarStatsCards scheduleData={scheduleData} />

          {loading ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-2 text-sm text-muted-foreground">{t("planning.legacy.calendar.loading")}</p>
              </CardContent>
            </Card>
          ) : scheduleData ? (
            <CalendarDayGrid scheduleData={scheduleData} />
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>{t("planning.legacy.calendar.noData")}</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!selectedEmployeeId && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>{t("planning.legacy.calendar.selectEmployeeHint")}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmployeeScheduleCalendar;
